/**
 * Clients keep their footage link until their first draft is ready (Ryan, 2026-10-26).
 *
 * We edit straight from the client's own link and keep no copy of their raw footage; clients were deleting it once
 * their order read "Files Processed" or "Editing In Progress". The server decides who is asked (backend
 * app/keep_link.py, from the project's own fields: a link of the client's own and no "Our Dropbox copy") and sends it
 * on a client's project as `keep_footage_link`:
 *
 *   'keep'  ask them to keep it (before the first draft is released)
 *   'done'  the first draft is theirs: they no longer need to
 *   null    it never applied (a hard drive, footage uploaded into the portal, Our Dropbox copy, a cancelled order)
 *
 * KEEP_LINE and DONE_LINE are the server's words too (its emails); backend tests/test_keep_footage_link.py reads this
 * file and holds the two equal. No em dashes; American English.
 */
export const KEEP_LINE = "Please keep your footage link shared, and the files in it, until your first draft is ready. We edit straight from your link and don't keep a copy of your raw footage.";
export const DONE_LINE = 'You no longer need to keep your footage link shared.';
// The short ask on a status description or a tooltip (lib/clientStatusHelp.js, lib/autoprep.js).
export const KEEP_TAIL = 'Your editor works from your footage link, so please keep it in place.';

export const KEEP = 'keep';
export const DONE = 'done';

/** True while the client is asked to keep their link. */
export const keepsLink = (project) => project?.keep_footage_link === KEEP;

/** True when the client's footage is a link of theirs (asked now, or asked until their first draft). */
export const sentLink = (project) => project?.keep_footage_link === KEEP || project?.keep_footage_link === DONE;

/** The board's line: shown once above a client's list while any of their open orders still needs its link. */
export const boardNeedsLine = (projects) => (projects || []).some(keepsLink);

// The board's Media icon, for a client. A link order is told about its link; a hard drive or a portal upload keeps the
// words it always had (we do hold that footage).
const MEDIA_LINK = { ready: 'Your footage link is ready for your editor', notReady: "We're waiting for your footage link to be ready" };
const MEDIA_OTHER = { ready: "We have your footage and it's ready to go", notReady: "We're still waiting to receive or process your footage" };
export function mediaTooltip(project, ready) {
  const t = sentLink(project) ? MEDIA_LINK : MEDIA_OTHER;
  return ready ? t.ready : t.notReady;
}
